// Maps the live Swiss Ephemeris Panchangam API response 1:1 into the shape the
// Panchangam page renders. All timestamps are Unix seconds — formatted in the
// target location's timezone via response.tz_str (never the browser's).
import { TITHI, NAKSHATRA, NAK_DEVATA, YOGA, KARANA, RASHI, VARA, PAKSHA, HORA_QUALITY, HORA_NOTE, VARA_DEVATA, TITHI_DEVATA } from "@/lib/panchangMaps";

const D = (map, key) => map[key] || map[(key || "").trim()] || key || "—";

function fmt(ts, tz) {
  if (ts == null) return "—";
  try {
    return new Date(ts * 1000).toLocaleTimeString("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit", hour12: true });
  } catch {
    return "—";
  }
}
const span = (a, b, tz) => `${fmt(a, tz)}–${fmt(b, tz)}`;

// Long form: "01:02 PM (Fri)" — used for tithi/nakshatra/yoga/karana ranges.
function fmtLong(ts, tz) {
  if (ts == null) return "—";
  try {
    const d = new Date(ts * 1000);
    const time = d.toLocaleTimeString("en-US", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: true });
    const day = d.toLocaleDateString("en-US", { timeZone: tz, weekday: "short" });
    return `${time} (${day})`;
  } catch {
    return "—";
  }
}
const rangeLong = (a, b, tz) => (a == null || b == null ? "" : `${fmtLong(a, tz)} - ${fmtLong(b, tz)}`);

function splitDisplay(s) {
  const [deva, en] = String(s || "").split(" · ");
  return { deva: (deva || "").trim(), en: (en || deva || "").trim() };
}

// Normalise a devata value from the API (string or {en,deva}) into {en,deva}
// for the bilingual cards. `fallbackDeva` supplies the Devanagari when the API
// sends only a plain English name.
function asDevataPair(v, fallbackDeva = "") {
  if (!v) return null;
  if (typeof v === "object") return { en: v.en || "", deva: v.deva || fallbackDeva || "" };
  return { en: String(v), deva: fallbackDeva || "" };
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
// Lunar month (masa) English → Devanagari, with common spelling variants.
const MASA = { Chaitra: "चैत्र", Vaishakha: "वैशाख", Vaisakha: "वैशाख", Jyeshtha: "ज्येष्ठ", Jyaistha: "ज्येष्ठ", Ashadha: "आषाढ़", Ashadh: "आषाढ़", Shravana: "श्रावण", Shraavana: "श्रावण", Bhadrapada: "भाद्रपद", Bhaadrapada: "भाद्रपद", Ashwin: "आश्विन", Ashwina: "आश्विन", Ashvin: "आश्विन", Kartika: "कार्तिक", Kartik: "कार्तिक", Margashirsha: "मार्गशीर्ष", Margashira: "मार्गशीर्ष", Pausha: "पौष", Paush: "पौष", Magha: "माघ", Maagha: "माघ", Phalguna: "फाल्गुन", Phalgun: "फाल्गुन" };
// English name (may carry a "Shukla/Krishna" paksha prefix) → {deva, en} pair.
const nextPair = (map, name) => {
  if (!name) return null;
  const n = String(name).trim();
  if (map[n]) return { deva: map[n], en: name };
  const deva = n.split(/\s+/).map((w) => map[w] || w).join(" ");
  return { deva, en: name };
};
const fmtDate = (t) => `${t.day} ${MONTHS[t.month - 1]} ${t.year}`;

function dayLength(a) {
  if (a.sunrise == null || a.sunset == null) return "";
  const s = Math.max(0, a.sunset - a.sunrise);
  return `${Math.floor(s / 3600)}h ${Math.round((s % 3600) / 60)}m`;
}

export function mapPanchang(r, { place, target }) {
  const tz = r.tz_str;
  const a = r.astro || {};
  const win = r.windows || {};
  const sam = r.samvat || {};
  const ti = r.tithi || {}, nk = r.nakshatra || {}, yo = r.yoga || {}, ka = r.karana || {}, ra = r.rashi || {}, va = r.vara || {};
  const ritu = splitDisplay(sam.ritu_display || sam.ritu);
  const horas = Array.isArray(r.horas) ? r.horas : [];
  const cur = horas.find((h) => h.isCurrent);
  const curDisp = cur ? splitDisplay(cur.display) : null;

  // Polar edge cases signalled by the API (high-latitude locations).
  const sunStatus = String(a.sun_status || "").trim().toUpperCase();
  const moonStatus = String(a.moon_status || "").trim().toUpperCase();
  let sunNote = "", moonNote = "";
  let sunrise = fmt(a.sunrise, tz), sunset = fmt(a.sunset, tz), day_length = dayLength(a);
  if (sunStatus === "POLAR_NIGHT") { sunNote = "Polar Night / No Sunrise"; sunrise = sunset = ""; day_length = "0h 0m"; }
  else if (sunStatus === "MIDNIGHT_SUN") { sunNote = "Midnight Sun / No Sunset"; sunrise = sunset = ""; day_length = "24h 0m"; }
  let moonrise = fmt(a.moonrise, tz), moonset = fmt(a.moonset, tz), moon_length = "";
  if (a.moonrise != null && a.moonset != null) {
    let s = a.moonset - a.moonrise;
    if (s < 0) s += 86400; // moonset falls on the following day
    moon_length = `${Math.floor(s / 3600)}h ${Math.round((s % 3600) / 60)}m`;
  }
  if (moonStatus === "CIRCUMPOLAR") { moonNote = "Circumpolar (Above Horizon)"; moonrise = moonset = ""; moon_length = ""; }
  else if (moonStatus === "NO_MOONRISE") { moonNote = "No Moonrise"; moonrise = moonset = ""; moon_length = ""; }

  // Polar edge case flag — true when the sun does not rise/set normally at this
  // latitude (drives the "midnight sun / polar night" notice on the page + homepage).
  const polar = sunStatus === "POLAR_NIGHT" || sunStatus === "MIDNIGHT_SUN";

  return {
    place,
    tz,
    is_live: !!target.isNow,
    computed_for: `${String(target.hour).padStart(2, "0")}:${String(target.min).padStart(2, "0")}`,
    date: fmtDate(target),
    weekday: va.name || "—",
    paksha: { en: ti.paksha || "", deva: D(PAKSHA, ti.paksha) },
    vikram_samvat: sam.vikram ?? "—",
    shaka_samvat: sam.shaka ?? "—",
    sauramana_samvat: sam.sauramana ?? sam.saura ?? "",
    samvat: sam,
    amanta: sam.amanta ? { deva: (MASA[sam.amanta] || sam.amanta), en: sam.amanta } : { deva: "", en: "" },
    purnimanta: sam.purnimanta ? { deva: (MASA[sam.purnimanta] || sam.purnimanta), en: sam.purnimanta } : { deva: "", en: "" },
    ritu: { deva: ritu.deva, en: ritu.en },
    hora: cur ? { deva: curDisp.deva, en: curDisp.en, span: span(cur.start, cur.end, tz), part: cur.phase } : null,
    tithi: { en: ti.name || "—", deva: D(TITHI, ti.name), number: ti.num, ends: fmt(ti.end, tz), range: rangeLong(ti.start, ti.end, tz), b: [ti.start, ti.end], next: nextPair({ ...PAKSHA, ...TITHI }, ti.next_name), devata: asDevataPair(ti.devata, TITHI_DEVATA[ti.name]?.deva) || TITHI_DEVATA[ti.name] || null },
    nakshatra: {
      en: nk.name || "—", deva: D(NAKSHATRA, nk.name), pada: nk.pada, ends: fmt(nk.end, tz), range: rangeLong(nk.start, nk.end, tz), b: [nk.start, nk.end], next: nextPair(NAKSHATRA, nk.next_name),
      devata: asDevataPair(nk.devata, D(NAK_DEVATA, nk.devata)),
    },
    // vara.devata comes straight from the Python API payload, pre-formatted as
    // "Adidevata (Pratyadidevata)" — render it exactly as received (payload is the
    // source of truth). Only if the payload omits it do we fall back to the map.
    vara: { en: va.name || "", deva: D(VARA, va.name), range: rangeLong(va.start, va.end, tz), b: [va.start, va.end], next: nextPair(VARA, va.next_name), devata: (va.devata ? (typeof va.devata === "object" ? { en: va.devata.en || "", deva: va.devata.deva || va.devata.en || "" } : { en: String(va.devata), deva: String(va.devata) }) : (VARA_DEVATA[va.name] || null)) },
    yoga: { en: yo.name || "—", deva: D(YOGA, yo.name), ends: fmt(yo.end, tz), range: rangeLong(yo.start, yo.end, tz), b: [yo.start, yo.end], next: nextPair(YOGA, yo.next_name) },
    karana: { en: ka.name || "—", deva: D(KARANA, ka.name), ends: fmt(ka.end, tz), range: rangeLong(ka.start, ka.end, tz), b: [ka.start, ka.end], next: nextPair(KARANA, ka.next_name) },
    sun_rashi: { en: ra.sun || "—", deva: D(RASHI, ra.sun) },
    moon_rashi: { en: ra.moon || "—", deva: D(RASHI, ra.moon) },
    muhurta: {
      brahma: win.brahma ? span(win.brahma.start, win.brahma.end, tz) : "",
      rahu_kalam: win.rahu ? span(win.rahu.start, win.rahu.end, tz) : "",
      yamaganda: win.yama ? span(win.yama.start, win.yama.end, tz) : "",
      gulika_kalam: win.gulika ? span(win.gulika.start, win.gulika.end, tz) : "",
      abhijit: win.abhijit ? span(win.abhijit.start, win.abhijit.end, tz) : "",
    },
    hora_timeline: horas.map((h) => {
      const dsp = splitDisplay(h.display);
      return { deva: dsp.deva, en: dsp.en, span: span(h.start, h.end, tz), part: h.phase, quality: h.quality || HORA_QUALITY[h.planet] || "neutral", current: !!h.isCurrent, note: HORA_NOTE[h.planet] || "" };
    }),
    choghadiya: null,
    sunrise, sunset, moonrise, moonset,
    polar,
    sun_note: sunNote, moon_note: moonNote,
    day_length, moon_length,
    note: "Computed astronomically (Swiss Ephemeris / True Lahiri Ayanamsa) for the given time and place.",
    ayanamsa: a.ayanamsa ?? "—",
  };
}
